/**
 * FullProfileBody — renders the active full-profile screen by (mode, tab).
 *
 * Analytics mode (ATabs): the 5 net-new analytics screens.
 * Profile mode (FTabs): the SAME modal tab components, reused verbatim (no fork)
 *   — Stats · Game Logs · Scouting · Info · News · Financials.
 * Lazy-loaded; the card's content area is the scroller.
 */
import React, { Suspense, lazy } from 'react';
import PropTypes from 'prop-types';
import { dynastyTheme } from '../../../../../../services/colorService';

const C = dynastyTheme.tokens.colors;

// Analytics screens (net-new)
const SummaryTab = lazy(() => import('./analytics/SummaryTab'));
const HeatmapTab = lazy(() => import('./analytics/HeatmapTab'));
const RankingsTab = lazy(() => import('./analytics/RankingsTab'));
const ImpactTab = lazy(() => import('./analytics/ImpactTab'));
const TrendsTab = lazy(() => import('./analytics/TrendsTab'));

// Profile screens — reuse the built modal tab components verbatim
const StatsTabContent = lazy(() => import('../../modal/StatsTabContent'));
const GameLogsTabContent = lazy(() => import('../../modal/GameLogsTabContent'));
const ScoutingTabContent = lazy(() => import('../../modal/ScoutingTabContent'));
const InfoTabContent = lazy(() => import('../../modal/InfoTabContent'));
const NewsTabContent = lazy(() => import('../../modal/NewsTabContent'));
const FinancialsTabContent = lazy(() => import('../../modal/FinancialsTabContent'));

const Fallback = () => <div style={{ padding: 40, textAlign: 'center', color: C.coolGray[650], fontSize: 12 }}>Loading…</div>;

const FullProfileBody = ({ mode, atab, ftab, player, playerId, leagueId, insights }) => {
  const ins = insights || {};
  const screen = () => {
    if (mode === 'analytics') {
      switch (atab) {
        case 'sum': return <SummaryTab player={player} ilRisk={ins.il} anomaly={ins.anomaly} comparables={ins.comparables} series={ins.series} fit={ins.fit} />;
        case 'heat': return <HeatmapTab player={player} />;
        case 'rank': return <RankingsTab player={player} />;
        case 'imp': return <ImpactTab player={player} impact={ins.impact} />;
        case 'trends': return <TrendsTab player={player} />;
        default: return <SummaryTab player={player} ilRisk={ins.il} anomaly={ins.anomaly} comparables={ins.comparables} series={ins.series} fit={ins.fit} />;
      }
    }
    // profile mode → reused modal components
    switch (ftab) {
      case 'stats': return <StatsTabContent player={player} playerId={playerId} leagueId={leagueId} statsSeasonYear={player?.stats_season_year} />;
      case 'logs': return <GameLogsTabContent player={player} playerId={playerId} leagueId={leagueId} fill />;
      case 'scouting': return <ScoutingTabContent player={player} />;
      case 'info': return <InfoTabContent player={player} />;
      case 'news': return <NewsTabContent playerId={playerId} />;
      case 'fin': return <FinancialsTabContent player={player} />;
      default: return <StatsTabContent player={player} playerId={playerId} leagueId={leagueId} statsSeasonYear={player?.stats_season_year} />;
    }
  };

  return <Suspense fallback={<Fallback />}>{screen()}</Suspense>;
};

FullProfileBody.propTypes = {
  mode: PropTypes.oneOf(['analytics', 'profile']).isRequired,
  atab: PropTypes.string,
  ftab: PropTypes.string,
  player: PropTypes.object,
  playerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  leagueId: PropTypes.string,
  insights: PropTypes.object,
};

export default FullProfileBody;
